import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, XCircle, Package, Users, ShoppingBag, TrendingUp, Eye, EyeOff, AlertTriangle, Lock, MessageCircle, Phone, CreditCard, ExternalLink, Shield, Trash2, UserX } from "lucide-react";
import API from "../api";
import { useAuth } from "../contexts/AuthContext";
import { formatPrice } from "../components/ProductCard";
import { formatWhatsApp } from "../utils/whatsapp";

const ADMIN_WA = "33777369462";

const BASE_TABS = [
  { id: "stats",          label: "Vue d'ensemble",  icon: TrendingUp  },
  { id: "orders",         label: "Commandes",        icon: ShoppingBag },
  { id: "subscriptions",  label: "Abonnements",      icon: CreditCard  },
  { id: "vendors",        label: "Vendeurs",          icon: Users       },
  { id: "products",       label: "Produits",          icon: Package     },
  { id: "leads",          label: "Leads",             icon: TrendingUp  },
  { id: "resets",         label: "Mot de passe",      icon: Phone       },
];
const SUPER_ADMIN_TAB = { id: "admins", label: "Admins", icon: Shield };

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-700", approved: "bg-green-100 text-green-700", paid: "bg-blue-100 text-blue-700",
  en_livraison: "bg-orange-100 text-orange-700", livré: "bg-green-100 text-green-700",
  annulé: "bg-red-100 text-red-700", initiated: "bg-yellow-100 text-yellow-700",
  awaiting_payment: "bg-blue-100 text-blue-700", payment_declared: "bg-teal-100 text-teal-700",
  payment_confirmed: "bg-green-100 text-green-700"
};
const STATUS_LABELS = {
  pending: "En attente", approved: "Approuvé", paid: "Payé", en_livraison: "En livraison", livré: "Livré", annulé: "Annulé",
  initiated: "Initiée", awaiting_payment: "Paiement requis", payment_declared: "Paiement déclaré",
  payment_confirmed: "Paiement confirmé"
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("stats");
  const TABS = user?.is_super_admin ? [...BASE_TABS, SUPER_ADMIN_TAB] : BASE_TABS;
  const [stats, setStats] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [leads, setLeads] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [subFilter, setSubFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [vendorFilter, setVendorFilter] = useState("pending");
  const [orderFilter, setOrderFilter] = useState("pending");
  const [actionMsg, setActionMsg] = useState("");

  useEffect(() => {
    if (!user) { navigate("/auth"); return; }
    if (user.role !== "admin") { navigate("/"); return; }
    Promise.all([
      API.get("/api/admin/stats"),
      API.get("/api/admin/vendors?status=pending"),
      API.get("/api/admin/products"),
      API.get("/api/admin/orders?status=pending"),
      API.get("/api/admin/leads"),
      API.get("/api/admin/subscriptions?status=pending"),
    ]).then(([s, v, p, o, l, sub]) => {
      setStats(s.data);
      setVendors(v.data || []);
      setProducts(p.data || []);
      setOrders(o.data || []);
      setLeads(l.data || []);
      setSubscriptions(sub.data || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, [user, navigate]);

  const loadOrders = async (filter) => {
    setOrderFilter(filter);
    const url = filter === "all" ? "/api/admin/orders" : `/api/admin/orders?status=${filter}`;
    const { data } = await API.get(url);
    setOrders(data || []);
  };

  const loadVendors = async (filter) => {
    setVendorFilter(filter);
    const url = filter === "all" ? "/api/admin/vendors" : `/api/admin/vendors?status=${filter}`;
    const { data } = await API.get(url);
    setVendors(data || []);
  };

  const approveOrder = async (id, approve) => {
    const endpoint = approve ? `/api/admin/orders/${id}/approve` : `/api/admin/orders/${id}/reject`;
    await API.put(endpoint);
    setOrders((prev) => prev.map((o) => o._id === id ? { ...o, status: approve ? "approved" : "annulé" } : o));
    setActionMsg(approve ? "Commande approuvée - infos de contact débloquées !" : "Commande rejetée.");
    setTimeout(() => setActionMsg(""), 3000);
  };

  const unlockLead = async (id) => {
    await API.put(`/api/admin/orders/${id}/unlock-lead`);
    setOrders((prev) => prev.map((o) => o._id === id ? { ...o, vendor_blocked: false } : o));
    setActionMsg("Lead débloqué — le vendeur peut maintenant voir les coordonnées client.");
    setTimeout(() => setActionMsg(""), 4000);
  };

  const handleSubscription = async (id, approve) => {
    const endpoint = approve
      ? `/api/admin/subscriptions/${id}/approve`
      : `/api/admin/subscriptions/${id}/reject`;
    await API.put(endpoint);
    setSubscriptions((prev) =>
      prev.map((s) => s._id === id ? { ...s, status: approve ? "approved" : "rejected" } : s)
    );
    setActionMsg(approve ? "Abonnement activé avec succès !" : "Demande d'abonnement rejetée.");
    setTimeout(() => setActionMsg(""), 3000);
  };

  const loadSubscriptions = async (filter) => {
    setSubFilter(filter);
    const url = filter === "all" ? "/api/admin/subscriptions" : `/api/admin/subscriptions?status=${filter}`;
    const { data } = await API.get(url);
    setSubscriptions(data || []);
  };

  const setVendorPlan = async (uid, plan) => {
    await API.put(`/api/admin/vendors/${uid}/set-plan`, { plan });
    setLeads((prev) => prev.map((v) => v.vendor_id === uid ? { ...v, plan, is_blocked: plan === "FREE" && v.leads_count >= v.leads_limit } : v));
    setActionMsg(`Plan mis à jour: ${plan}`);
    setTimeout(() => setActionMsg(""), 3000);
  };

  const approveVendor = async (uid, approve) => {
    const endpoint = approve ? `/api/admin/vendors/${uid}/approve` : `/api/admin/vendors/${uid}/reject`;
    await API.put(endpoint);
    setVendors((prev) => prev.filter((v) => v.user_id !== uid));
    const { data } = await API.get("/api/admin/stats");
    setStats(data);
    setActionMsg(approve ? "Vendeur approuvé avec succès !" : "Vendeur rejeté.");
    setTimeout(() => setActionMsg(""), 3000);
  };

  const deleteVendor = async (uid, shopName) => {
    if (!window.confirm(`Supprimer définitivement "${shopName}" ? Cette action est irréversible.`)) return;
    await API.delete(`/api/admin/vendors/${uid}`);
    setVendors((prev) => prev.filter((v) => v.user_id !== uid));
    const { data } = await API.get("/api/admin/stats");
    setStats(data);
    setActionMsg(`Vendeur "${shopName}" supprimé définitivement.`);
    setTimeout(() => setActionMsg(""), 3000);
  };

  const toggleProduct = async (id) => {
    const { data } = await API.put(`/api/admin/products/${id}/toggle`);
    setProducts((prev) => prev.map((p) => p._id === id ? { ...p, is_active: data.is_active } : p));
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#FF9900] border-t-transparent" />
    </div>
  );

  return (
    <div className="bg-[#F9FAFB] min-h-screen pb-24 md:pb-8">
      <div className="w-full max-w-6xl mx-auto px-4 py-4">
        <div className="mb-4">
          <h1 className="text-xl font-extrabold text-[#232F3E]">Administration Jokkoleen</h1>
          <p className="text-sm text-gray-500">Bienvenue, {user?.name}</p>
        </div>

        {actionMsg && (
          <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-md p-3 mb-4 flex items-center gap-2" data-testid="action-message">
            <CheckCircle size={14} /> {actionMsg}
          </div>
        )}

        {/* Tabs */}
        <div className="flex overflow-x-auto gap-1 mb-5 bg-white rounded-md border border-gray-200 p-1">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-1.5 shrink-0 px-3 py-2 rounded text-xs font-semibold transition-colors ${tab === id ? "bg-[#232F3E] text-white" : "text-gray-600 hover:bg-gray-50"}`}
              data-testid={`admin-tab-${id}`}
            >
              <Icon size={13} /> {label}
              {id === "vendors" && vendorFilter === "pending" && stats?.pending_vendors > 0 && (
                <span className="bg-red-500 text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center font-bold">{stats.pending_vendors}</span>
              )}
            </button>
          ))}
        </div>

        {/* Stats */}
        {tab === "stats" && stats && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                { label: "Clients", value: stats.total_customers, icon: Users, color: "bg-blue-50 text-blue-600" },
                { label: "Vendeurs approuvés", value: stats.approved_vendors, icon: CheckCircle, color: "bg-green-50 text-green-600" },
                { label: "En attente d'approbation", value: stats.pending_vendors, icon: AlertTriangle, color: "bg-yellow-50 text-yellow-600" },
                { label: "Total produits", value: stats.total_products, icon: Package, color: "bg-purple-50 text-purple-600" },
                { label: "Total commandes", value: stats.total_orders, icon: ShoppingBag, color: "bg-orange-50 text-orange-600" },
                { label: "Commandes en attente", value: stats.pending_orders, icon: AlertTriangle, color: "bg-red-50 text-red-600" },
              ].map(({ label, value, icon: Icon, color }) => (
                <div key={label} className="bg-white border border-gray-200 rounded-md p-4" data-testid={`admin-stat-${label}`}>
                  <div className={`w-9 h-9 rounded-lg ${color} flex items-center justify-center mb-2`}>
                    <Icon size={18} />
                  </div>
                  <p className="text-2xl font-black text-[#232F3E]">{value}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{label}</p>
                </div>
              ))}
            </div>

            {/* Funnel analytique */}
            <div className="bg-white border border-gray-200 rounded-md p-4">
              <h3 className="text-sm font-bold text-[#232F3E] mb-3 flex items-center gap-2">
                <TrendingUp size={14} className="text-[#FF9900]" /> Funnel WhatsApp-first
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: "Vues produits", value: stats.total_product_views || 0, color: "text-blue-600", bg: "bg-blue-50" },
                  { label: "Clics Commander", value: stats.total_click_orders || 0, color: "text-orange-600", bg: "bg-orange-50" },
                  { label: "Intentions créées", value: stats.total_initiated || 0, color: "text-purple-600", bg: "bg-purple-50" },
                  { label: "Paiements déclarés", value: stats.total_payment_declared || 0, color: "text-green-600", bg: "bg-green-50" },
                ].map(({ label, value, color, bg }) => (
                  <div key={label} className={`${bg} rounded-md p-3 text-center`} data-testid={`funnel-stat-${label}`}>
                    <p className={`text-xl font-black ${color}`}>{value}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{label}</p>
                  </div>
                ))}
              </div>
              {stats.total_click_orders > 0 && (
                <p className="text-xs text-gray-400 mt-2 text-center">
                  Taux conversion clics → paiements : <span className="font-bold text-[#232F3E]">
                    {Math.round((stats.total_payment_declared || 0) / stats.total_click_orders * 100)}%
                  </span>
                </p>
              )}
            </div>
          </div>
        )}

        {/* Vendors */}
        {tab === "vendors" && (
          <div>
            <div className="flex flex-wrap gap-2 mb-4">
              {[
                ["pending",  "En attente"],
                ["approved", "Approuvés"],
                ["rejected", "Rejetés"],
                ["all",      "Tous"]
              ].map(([v, l]) => (
                <button
                  key={v}
                  onClick={() => loadVendors(v)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${vendorFilter === v ? "bg-[#232F3E] text-white border-[#232F3E]" : "bg-white border-gray-300 text-gray-600 hover:border-[#232F3E]"}`}
                  data-testid={`vendor-filter-${v}`}
                >
                  {l}
                </button>
              ))}
            </div>

            {vendors.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-md border border-gray-200">
                <Users size={40} className="text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">Aucun vendeur dans cette catégorie</p>
              </div>
            ) : (
              <div className="space-y-3">
                {vendors.map((vendor) => {
                  const isApproved = vendor.is_approved;
                  const isRejected = vendor.vendor_status === "rejected";
                  return (
                  <div key={vendor._id} className="bg-white border border-gray-200 rounded-md p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="admin-vendor-item">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="font-bold text-[#232F3E] text-sm">{vendor.shop_name}</p>
                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${isApproved ? "bg-green-100 text-green-700" : isRejected ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"}`}>
                          {isApproved ? "Approuvé" : isRejected ? "Rejeté" : "En attente"}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">{vendor.user_name} · {vendor.user_whatsapp || vendor.user_email}</p>
                      {vendor.description && <p className="text-xs text-gray-400 mt-1 line-clamp-1">{vendor.description}</p>}
                    </div>
                    <div className="flex gap-2 shrink-0 flex-wrap">
                      {!isApproved && !isRejected && (
                        <>
                          <button
                            onClick={() => approveVendor(vendor.user_id, true)}
                            className="flex items-center gap-1.5 bg-green-600 text-white font-bold px-3 py-2 rounded-md text-xs hover:bg-green-700 transition-colors"
                            data-testid="approve-vendor-btn"
                          >
                            <CheckCircle size={13} /> Approuver
                          </button>
                          <button
                            onClick={() => approveVendor(vendor.user_id, false)}
                            className="flex items-center gap-1.5 bg-red-50 text-red-600 border border-red-200 font-bold px-3 py-2 rounded-md text-xs hover:bg-red-100 transition-colors"
                            data-testid="reject-vendor-btn"
                          >
                            <XCircle size={13} /> Rejeter
                          </button>
                        </>
                      )}
                      {isApproved && (
                        <button
                          onClick={() => approveVendor(vendor.user_id, false)}
                          className="flex items-center gap-1.5 bg-red-50 text-red-600 border border-red-200 font-medium px-3 py-1.5 rounded-md text-xs hover:bg-red-100 transition-colors"
                          data-testid="suspend-vendor-btn"
                        >
                          <XCircle size={12} /> Suspendre
                        </button>
                      )}
                      {isRejected && (
                        <button
                          onClick={() => approveVendor(vendor.user_id, true)}
                          className="flex items-center gap-1.5 bg-green-50 text-green-600 border border-green-200 font-medium px-3 py-1.5 rounded-md text-xs hover:bg-green-100 transition-colors"
                          data-testid="reapprove-vendor-btn"
                        >
                          <CheckCircle size={12} /> Approuver
                        </button>
                      )}
                      <button
                        onClick={() => deleteVendor(vendor.user_id, vendor.shop_name)}
                        className="flex items-center gap-1.5 bg-gray-50 text-gray-500 border border-gray-200 font-medium px-2.5 py-1.5 rounded-md text-xs hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
                        data-testid="delete-vendor-btn"
                        title="Supprimer définitivement"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Products */}
        {tab === "products" && (
          <div className="space-y-2">
            <p className="text-sm text-gray-500 mb-3">{products.length} produit{products.length > 1 ? "s" : ""} au total</p>
            {products.map((p) => (
              <div key={p._id} className="bg-white border border-gray-200 rounded-md p-3 flex items-center gap-3" data-testid="admin-product-item">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-[#232F3E] truncate">{p.name}</p>
                  <p className="text-xs text-gray-400">{p.vendor_shop_name} · {formatPrice(p.price)}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded font-medium shrink-0 ${p.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                  {p.is_active ? "Actif" : "Désactivé"}
                </span>
                <button
                  onClick={() => toggleProduct(p._id)}
                  className={`p-1.5 rounded transition-colors shrink-0 ${p.is_active ? "text-red-500 hover:bg-red-50" : "text-green-600 hover:bg-green-50"}`}
                  data-testid="toggle-product-btn"
                >
                  {p.is_active ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Orders with approval */}
        {tab === "orders" && (
          <div className="space-y-3">
            {/* Filtre de statut */}
            <div className="flex gap-2 mb-4">
              {[["pending", "En attente"], ["approved", "Approuvées"], ["all", "Toutes"]].map(([v, l]) => (
                <button
                  key={v}
                  onClick={() => loadOrders(v)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${orderFilter === v ? "bg-[#232F3E] text-white border-[#232F3E]" : "bg-white border-gray-300 text-gray-600 hover:border-[#232F3E]"}`}
                  data-testid={`order-filter-${v}`}
                >
                  {l}
                </button>
              ))}
              <span className="ml-auto text-xs text-gray-400 self-center">{orders.length} commande{orders.length > 1 ? "s" : ""}</span>
            </div>

            {orders.length === 0 ? (
              <div className="text-center py-10 bg-white rounded-md border border-gray-200">
                <ShoppingBag size={36} className="text-gray-200 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">Aucune commande dans cette catégorie</p>
              </div>
            ) : orders.map((order) => (
              <div key={order._id} className="bg-white border border-gray-200 rounded-md p-4" data-testid="admin-order-item">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-bold text-sm text-[#232F3E]">#{order._id?.slice(-6).toUpperCase()}</p>
                  <div className="flex items-center gap-2">
                    {order.vendor_blocked && (
                      <span className="text-[10px] bg-orange-100 text-orange-700 font-bold px-1.5 py-0.5 rounded">Lead bloqué</span>
                    )}
                    <span className={`text-xs font-medium px-2 py-0.5 rounded ${STATUS_COLORS[order.status] || "bg-gray-100 text-gray-600"}`}>
                      {STATUS_LABELS[order.status] || order.status}
                    </span>
                  </div>
                </div>

                <div className="text-xs text-gray-500 mb-1">
                  <span className="font-medium">{order.customer_name}</span>
                  {order.customer_phone && <span> · <Phone size={9} className="inline" /> {order.customer_phone}</span>}
                  {" · "}{new Date(order.created_at).toLocaleDateString("fr-FR")}
                </div>
                <p className="text-xs text-gray-600 mb-1">{order.vendor_shop_name || "Vendeur inconnu"}</p>
                <p className="text-sm font-black text-[#232F3E] mb-3">{formatPrice(order.total_amount)}</p>

                <div className="flex flex-wrap gap-2">
                  {order.status === "pending" && (
                    <>
                      <button
                        onClick={() => approveOrder(order._id, true)}
                        className="flex items-center gap-1.5 bg-green-600 text-white font-bold px-3 py-2 rounded-md text-xs hover:bg-green-700 transition-colors"
                        data-testid="approve-order-btn"
                      >
                        <CheckCircle size={13} /> Autoriser paiement
                      </button>
                      <button
                        onClick={() => approveOrder(order._id, false)}
                        className="flex items-center gap-1.5 bg-red-50 text-red-600 border border-red-200 font-medium px-3 py-2 rounded-md text-xs hover:bg-red-100 transition-colors"
                        data-testid="reject-order-btn"
                      >
                        <XCircle size={13} /> Rejeter
                      </button>
                    </>
                  )}
                  {order.vendor_blocked && order.status !== "pending" && (
                    <button
                      onClick={() => unlockLead(order._id)}
                      className="flex items-center gap-1.5 bg-orange-500 text-white font-bold px-3 py-2 rounded-md text-xs hover:bg-orange-600 transition-colors"
                      data-testid="unlock-lead-admin-btn"
                    >
                      <Lock size={12} /> Débloquer lead
                    </button>
                  )}
                  {order.vendor_whatsapp && (
                    <a
                      href={`https://wa.me/${formatWhatsApp(order.vendor_whatsapp)}?text=${encodeURIComponent(`Bonjour, vous avez un nouveau lead Jokkoleen #${order._id?.slice(-6).toUpperCase()} en attente.`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 bg-[#25D366] text-white font-bold px-3 py-2 rounded-md text-xs hover:bg-[#20BD5A]"
                      data-testid="notify-vendor-btn"
                    >
                      <MessageCircle size={12} /> Notifier vendeur
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Subscriptions management */}
        {tab === "subscriptions" && (
          <div className="space-y-3">
            <div className="flex gap-2 mb-4">
              {[["pending", "En attente"], ["approved", "Validées"], ["all", "Toutes"]].map(([v, l]) => (
                <button
                  key={v}
                  onClick={() => loadSubscriptions(v)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${subFilter === v ? "bg-[#232F3E] text-white border-[#232F3E]" : "bg-white border-gray-300 text-gray-600 hover:border-[#232F3E]"}`}
                  data-testid={`sub-filter-${v}`}
                >
                  {l}
                </button>
              ))}
              <span className="ml-auto text-xs text-gray-400 self-center">{subscriptions.length} demande{subscriptions.length > 1 ? "s" : ""}</span>
            </div>

            {subscriptions.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-md border border-gray-200">
                <CreditCard size={36} className="text-gray-200 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">Aucune demande d'abonnement dans cette catégorie</p>
              </div>
            ) : subscriptions.map((sub) => (
              <div key={sub._id} className="bg-white border border-gray-200 rounded-md p-4" data-testid="admin-sub-item">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-bold text-sm text-gray-900">{sub.vendor_name}</p>
                    <p className="text-xs text-gray-500">{sub.shop_name} · {sub.whatsapp}</p>
                    <p className="text-xs text-gray-400">{new Date(sub.created_at).toLocaleDateString("fr-FR")}</p>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded block mb-1 ${sub.plan === "PREMIUM" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>
                      {sub.plan}
                    </span>
                    <span className="text-xs font-black text-gray-900">{(sub.amount || 0).toLocaleString("fr-FR")} FCFA</span>
                    <p className="text-[10px] text-gray-400">{sub.payment_method === "wave" ? "Wave" : "Orange Money"}</p>
                  </div>
                </div>

                {/* Proof image */}
                {sub.proof_image_url && (
                  <a
                    href={sub.proof_image_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-xs text-[#16A34A] font-semibold mb-3 hover:underline"
                    data-testid="view-proof-link"
                  >
                    <ExternalLink size={12} /> Voir la preuve de paiement
                  </a>
                )}

                {/* Actions */}
                {sub.status === "pending" && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleSubscription(sub._id, true)}
                      className="flex items-center gap-1.5 bg-green-600 text-white font-bold px-3 py-2 rounded-md text-xs hover:bg-green-700"
                      data-testid="approve-sub-btn"
                    >
                      <CheckCircle size={13} /> Valider le plan {sub.plan}
                    </button>
                    <button
                      onClick={() => handleSubscription(sub._id, false)}
                      className="flex items-center gap-1.5 bg-red-50 text-red-600 border border-red-200 font-medium px-3 py-2 rounded-md text-xs hover:bg-red-100"
                      data-testid="reject-sub-btn"
                    >
                      <XCircle size={13} /> Refuser
                    </button>
                    <a
                      href={`https://wa.me/${formatWhatsApp(sub.whatsapp)}?text=${encodeURIComponent(`Bonjour ! Votre plan ${sub.plan} Jokkoleen a été validé. Bienvenue dans le club !`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 bg-[#25D366] text-white font-bold px-3 py-2 rounded-md text-xs hover:bg-[#20BD5A] ml-auto"
                      data-testid="notify-vendor-sub-btn"
                    >
                      <MessageCircle size={12} /> Notifier
                    </a>
                  </div>
                )}
                {sub.status !== "pending" && (
                  <span className={`text-xs font-bold px-3 py-1.5 rounded-md ${sub.status === "approved" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
                    {sub.status === "approved" ? "Validé" : "Refusé"}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Leads management */}
        {tab === "leads" && (
          <div>
            <div className="grid grid-cols-3 gap-3 mb-5">
              {[
                { label: "Vendeurs bloqués (FREE)", value: stats?.blocked_vendors || 0, color: "bg-red-50 text-red-600" },
                { label: "Vendeurs PRO", value: stats?.pro_vendors || 0, color: "bg-blue-50 text-blue-600" },
                { label: "Revenus estimés/mois", value: `${(stats?.estimated_revenue || 0).toLocaleString("fr-FR")} FCFA`, color: "bg-green-50 text-green-600" },
              ].map(({ label, value, color }) => (
                <div key={label} className="bg-white border border-gray-200 rounded-md p-3 text-center">
                  <p className={`text-lg font-black ${color.split(" ")[1]}`}>{value}</p>
                  <p className="text-xs text-gray-500">{label}</p>
                </div>
              ))}
            </div>
            <div className="space-y-2">
              {leads.map((lead) => (
                <div key={lead.vendor_id} className="bg-white border border-gray-200 rounded-md p-3 flex items-center gap-3" data-testid="lead-item">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-gray-900 truncate">{lead.shop_name}</p>
                    <p className="text-xs text-gray-500">{lead.user_name} · {lead.leads_count}/{lead.leads_limit === 999999 ? "∞" : lead.leads_limit} leads</p>
                  </div>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded shrink-0 ${
                    lead.plan === "PREMIUM" ? "bg-purple-100 text-purple-700" :
                    lead.plan === "PRO" ? "bg-blue-100 text-blue-600" :
                    lead.is_blocked ? "bg-red-100 text-red-600" : "bg-gray-100 text-gray-600"
                  }`}>
                    {lead.is_blocked ? "🔒 BLOQUÉ" : lead.plan}
                  </span>
                  <div className="flex gap-1 shrink-0">
                    {["FREE", "PRO", "PREMIUM"].map((plan) => (
                      <button
                        key={plan}
                        onClick={() => setVendorPlan(lead.vendor_id, plan)}
                        disabled={lead.plan === plan}
                        className={`text-[10px] font-bold px-2 py-1 rounded border transition-colors ${lead.plan === plan ? "bg-gray-800 text-white cursor-default" : "bg-white hover:bg-gray-50 text-gray-600"}`}
                        data-testid={`set-plan-${plan}`}
                      >
                        {plan}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Password reset requests */}
        {tab === "resets" && <PasswordResetsTab />}

        {/* Gestion des admins — super-admin uniquement */}
        {tab === "admins" && user?.is_super_admin && <AdminsTab currentUserId={user?.id || user?._id} />}
      </div>
    </div>
  );
}

function PasswordResetsTab() {
  const [resets, setResets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/api/admin/password-resets")
      .then((r) => setResets(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-8 text-gray-400 text-sm">Chargement...</div>;

  return (
    <div>
      <p className="text-sm text-gray-500 mb-4">
        Demandes de réinitialisation de mot de passe en attente. Contactez le vendeur sur WhatsApp avec son code pour confirmer l'identité.
      </p>
      {resets.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-md border border-gray-200">
          <p className="text-gray-500 text-sm">Aucune demande en attente</p>
        </div>
      ) : (
        <div className="space-y-2">
          {resets.map((r) => (
            <div key={r._id} className="bg-white border border-gray-200 rounded-md p-4 flex items-center gap-3" data-testid="reset-item">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-gray-900">{r.name || "Vendeur"}</p>
                <p className="text-xs text-gray-500">WhatsApp : {r.whatsapp}</p>
                <p className="text-xs text-gray-400">{new Date(r.created_at).toLocaleString("fr-FR")}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs text-gray-500 mb-1">Code :</p>
                <p className="text-xl font-black text-[#232F3E] tracking-widest">{r.token}</p>
              </div>
              <a
                href={`https://wa.me/${formatWhatsApp(r.whatsapp)}?text=${encodeURIComponent(`Bonjour ${r.name || ""}, votre code de réinitialisation Jokkoleen est : ${r.token}. Ce code expire dans 2h.`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#25D366] text-white text-xs font-bold px-3 py-2 rounded-lg hover:bg-[#20BD5A] shrink-0"
                data-testid="send-reset-code-btn"
              >
                Envoyer le code
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminsTab({ currentUserId }) {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const loadAdmins = () => {
    setLoading(true);
    API.get("/api/admin/admins")
      .then((r) => setAdmins(r.data || []))
      .catch(() => setError("Impossible de charger la liste des admins"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadAdmins(); }, []);

  const handleDelete = async (admin) => {
    if (!window.confirm(`Supprimer l'admin "${admin.name}" ? Cette action est irréversible.`)) return;
    setDeleting(admin.id);
    setError("");
    try {
      await API.delete(`/api/admin/admins/${admin.id}`);
      setMsg(`Admin "${admin.name}" supprimé avec succès.`);
      setAdmins((prev) => prev.filter((a) => a.id !== admin.id));
      setTimeout(() => setMsg(""), 4000);
    } catch (e) {
      setError(e.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setDeleting(null);
    }
  };

  if (loading) return <div className="text-center py-8 text-gray-400 text-sm">Chargement...</div>;

  return (
    <div data-testid="admins-tab">
      <div className="flex items-center gap-2 mb-4">
        <Shield size={16} className="text-[#232F3E]" />
        <p className="text-sm font-semibold text-[#232F3E]">Gestion des administrateurs</p>
        <span className="text-xs text-gray-400">— réservé au super-admin</span>
      </div>

      {msg && (
        <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-md p-3 mb-4 flex items-center gap-2" data-testid="admins-success-msg">
          <CheckCircle size={14} /> {msg}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md p-3 mb-4 flex items-center gap-2" data-testid="admins-error-msg">
          <XCircle size={14} /> {error}
        </div>
      )}

      {admins.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-md border border-gray-200">
          <p className="text-gray-500 text-sm">Aucun administrateur trouvé</p>
        </div>
      ) : (
        <div className="space-y-2">
          {admins.map((admin) => {
            const isSelf = admin.id === currentUserId;
            const isSuperAdmin = admin.is_super_admin;
            const canDelete = !isSelf && !isSuperAdmin;
            const identifier = admin.whatsapp || admin.email || "—";
            return (
              <div
                key={admin.id}
                className="bg-white border border-gray-200 rounded-md p-4 flex items-center gap-3"
                data-testid={`admin-item-${admin.id}`}
              >
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0 ${isSuperAdmin ? "bg-[#232F3E]" : "bg-gray-400"}`}>
                  {admin.name ? admin.name[0].toUpperCase() : "A"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-sm text-gray-900 truncate">{admin.name || "Admin"}</p>
                    {isSuperAdmin && (
                      <span className="bg-[#232F3E] text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide" data-testid="super-admin-badge">
                        Super-Admin
                      </span>
                    )}
                    {isSelf && (
                      <span className="bg-blue-100 text-blue-600 text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">
                        Vous
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 truncate">{identifier}</p>
                  {admin.created_at && (
                    <p className="text-xs text-gray-400">{new Date(admin.created_at).toLocaleDateString("fr-FR")}</p>
                  )}
                </div>
                <div className="shrink-0">
                  {canDelete ? (
                    <button
                      onClick={() => handleDelete(admin)}
                      disabled={deleting === admin.id}
                      className="flex items-center gap-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-semibold px-3 py-2 rounded-lg transition-colors disabled:opacity-50"
                      data-testid={`delete-admin-btn-${admin.id}`}
                    >
                      {deleting === admin.id ? (
                        <div className="w-3 h-3 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Trash2 size={13} />
                      )}
                      Supprimer
                    </button>
                  ) : (
                    <span className="flex items-center gap-1 text-xs text-gray-300 px-2 py-1">
                      <Lock size={12} /> Protégé
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
